import type { Source } from '../../src/engine/schema.js';

/**
 * Every source carries a retrieval date. An undated source is not a source,
 * and the validator enforces it.
 *
 * Supporting quotes are capped at 25 words — enough to show the sentence that
 * backs a number, short enough not to reproduce the document.
 */
export const sources: Source[] = [
  // ------------------------------------------------------------ city of pine island
  {
    id: 'src_city_skyway',
    title: 'Project Skyway',
    publisher: 'City of Pine Island, Minnesota',
    url: 'https://pineislandmn.gov/skyway',
    retrievedAt: '2026-08-04',
    kind: 'local-government',
    notes:
      'The project page. Hosts the Final AUAR, noise study, traffic study, land use applications, Summary of Subsidy and the Economic Impact Study.',
  },
  {
    id: 'src_city_subsidy',
    title: 'Summary of Subsidy',
    publisher: 'City of Pine Island, Minnesota',
    url: 'https://pineislandmn.gov/vertical/sites/%7B52A5D060-3422-4069-8E86-A961C2752B7F%7D/uploads/Summary_of_Subsidy.pdf',
    publishedAt: '2026-01-20',
    retrievedAt: '2026-08-04',
    kind: 'local-government',
    locator: 'Presented to Council 2026-01-20; abatement approved 2026-02-04',
    notes:
      'Source for gross projected taxes, abatement amount, Community Betterment Fund and the Panther Program school payments.',
  },
  {
    id: 'src_city_econ_impact',
    title: 'Economic Impact Study Presentation',
    publisher: 'University of Minnesota Extension (Aiden Opatz), for the City of Pine Island',
    url: 'https://pineislandmn.gov/vertical/sites/%7B52A5D060-3422-4069-8E86-A961C2752B7F%7D/uploads/Economic_Impact_Study_Presentation.pdf',
    publishedAt: '2026-01-20',
    retrievedAt: '2026-08-04',
    kind: 'local-government',
    locator: 'Direct construction employment by MEP-share scenario',
    notes:
      'IMPLAN-based. Assumes $700M over two years, Goodhue County region. Employment figures are JOB-YEARS, not simultaneous jobs.',
  },
  {
    id: 'src_city_tnt_2026',
    title: 'Budget and Levy Hearing for Taxes Payable 2026',
    publisher: 'City of Pine Island, Minnesota',
    url: 'https://pineislandmn.gov/vertical/sites/%7B52A5D060-3422-4069-8E86-A961C2752B7F%7D/uploads/Pine_Island_TNT_2026_-_Power_Point.pdf',
    publishedAt: '2025-12-16',
    retrievedAt: '2026-08-04',
    kind: 'local-government',
  },
  {
    id: 'src_city_noise',
    title: 'Project Skyway Noise Level Study',
    publisher: 'City of Pine Island, Minnesota',
    url: 'https://pineislandmn.gov/vertical/sites/%7B52A5D060-3422-4069-8E86-A961C2752B7F%7D/uploads/Noise.pdf',
    retrievedAt: '2026-08-04',
    kind: 'local-government',
  },
  {
    id: 'src_city_fire',
    title: 'Pine Island Fire & Rescue',
    publisher: 'City of Pine Island, Minnesota',
    url: 'https://pineislandmn.gov/fire',
    retrievedAt: '2026-08-04',
    kind: 'local-government',
    notes: 'All-volunteer department covering roughly 130 square miles across five townships.',
  },

  // ------------------------------------------------------------ county / school
  {
    id: 'src_goodhue_rates',
    title: 'Tax Extension Rates by Fund, Payable 2026',
    publisher: 'Goodhue County, Minnesota',
    url: 'https://goodhuecountymn.gov/files/budgets/tax_rates_tax_rate_document_payable_2026_87253.pdf',
    publishedAt: '2026-02-18',
    retrievedAt: '2026-08-04',
    kind: 'local-government',
    locator: 'District 6801, Pine Island City',
  },
  {
    id: 'src_goodhue_budget',
    title: '2026 Approved Budget',
    publisher: 'Goodhue County, Minnesota',
    url: 'https://goodhuecountymn.gov/files/budgets/budget_details_2026_53858.pdf',
    publishedAt: '2025-12-16',
    retrievedAt: '2026-08-04',
    kind: 'local-government',
  },
  {
    id: 'src_isd255_budget',
    title: 'District Revenues and Expenditures, FY2024 actual / FY2025 budget',
    publisher: 'Pine Island Public Schools, ISD 255',
    url: 'https://pineisland.ss8.sharpschool.com/district/budget',
    retrievedAt: '2026-08-04',
    kind: 'local-government',
    notes: 'Published under the Minn. Stat. § 123B.10 disclosure requirement.',
  },
  {
    id: 'src_pelsb_salary',
    title: 'Licensed Average Salary by District, 2025-26',
    publisher: 'Minnesota Professional Educator Licensing and Standards Board',
    url: 'https://mn.gov/pelsb/board/data/',
    retrievedAt: '2026-08-04',
    kind: 'agency-data',
    notes:
      'Contract salary only — excludes benefits, extra duty and overloads. That is precisely why it is the right base to apply a benefit load to.',
  },

  // ------------------------------------------------------------ statute
  {
    id: 'src_mn_state_general',
    title: 'State General Property Tax',
    publisher: 'Minnesota Department of Revenue',
    url: 'https://www.revenue.state.mn.us/state-general-property-tax',
    retrievedAt: '2026-08-04',
    kind: 'agency-data',
    notes:
      'Commercial-industrial rate for payable 2026 on a fixed statewide levy. The first $150,000 of market value per parcel is excluded, and only once across contiguous parcels in common ownership.',
  },
  {
    id: 'src_stat_272_02',
    title: 'Minn. Stat. § 272.02 — Exempt Property',
    publisher: 'Minnesota Office of the Revisor of Statutes',
    url: 'https://www.revisor.mn.gov/statutes/cite/272.02',
    retrievedAt: '2026-08-04',
    kind: 'statute',
    locator: 'subd. 9',
    notes:
      'Minnesota exempts business personal property generally, for every industry. Data center IT equipment is therefore outside the property tax base. This is not a data center incentive.',
  },
  {
    id: 'src_stat_469_1813',
    title: 'Minn. Stat. § 469.1813 — Abatement Authority',
    publisher: 'Minnesota Office of the Revisor of Statutes',
    url: 'https://www.revisor.mn.gov/statutes/cite/469.1813',
    retrievedAt: '2026-08-04',
    kind: 'statute',
    notes:
      'Caps a standard abatement at 15 years and at the greater of 10% of net tax capacity or $200,000 per year. The city describes a 28-year term, which needs explaining.',
  },
  {
    id: 'src_stat_297a68',
    title: 'Minn. Stat. § 297A.68 subd. 42 — Data Centers',
    publisher: 'Minnesota Office of the Revisor of Statutes',
    url: 'https://www.revisor.mn.gov/statutes/cite/297A.68',
    retrievedAt: '2026-08-04',
    kind: 'statute',
    notes:
      'As amended by Laws 2025, 1st Spec. Sess., ch. 12 § 17. Electricity lost the exemption 2025-07-01; term extended to 35 years; prevailing wage required for large-scale data centers. NOTE: the Department of Revenue guidance page still shows the repealed 20-year term. The statute controls.',
  },
  {
    id: 'src_stat_216b72',
    title: 'Minn. Stat. § 216B.72 — Large-Scale Data Center Fee',
    publisher: 'Minnesota Office of the Revisor of Statutes',
    url: 'https://www.revisor.mn.gov/statutes/cite/216B.72',
    retrievedAt: '2026-08-04',
    kind: 'statute',
    notes:
      'Tiered annual fee by load, deposited to the energy conservation account. The amount an individual facility pays is non-public by statute.',
  },
  {
    id: 'src_stat_178_036',
    title: 'Minn. Stat. § 178.036 — Apprenticeship Ratios',
    publisher: 'Minnesota Office of the Revisor of Statutes',
    url: 'https://www.revisor.mn.gov/statutes/cite/178.036',
    retrievedAt: '2026-08-04',
    kind: 'statute',
    locator: 'subd. 5',
    notes:
      'A collective bargaining agreement governs where one exists. Absent that, building trades default to one apprentice for the first journeyworker plus one per additional three. Ratios are ceilings, not floors.',
  },
  {
    id: 'src_tra_rates',
    title: 'Employer Contribution Rates; Minn. Stat. § 354.42 subd. 3',
    publisher: 'Minnesota Teachers Retirement Association',
    url: 'https://minnesotatra.org/employers/contribution-rates',
    retrievedAt: '2026-08-04',
    kind: 'agency-data',
    notes:
      'Employer rate effective 2025-07-01 for Coordinated members. Minnesota House Research materials still cite the earlier 9.5% figure.',
  },
  {
    id: 'src_mn_rule_7030',
    title: 'Minn. R. ch. 7030 — Noise Pollution Control',
    publisher: 'Minnesota Office of the Revisor of Statutes',
    url: 'https://www.revisor.mn.gov/rules/7030/',
    retrievedAt: '2026-08-04',
    kind: 'statute',
    notes:
      'NAC-1 residential standards are stated as both L10 and L50. L50 is the level exceeded half the hour, which is the binding measure for a source that runs continuously.',
  },
  {
    id: 'src_mpca_noise',
    title: 'A Guide to Noise Control in Minnesota',
    publisher: 'Minnesota Pollution Control Agency',
    url: 'https://www.pca.state.mn.us/sites/default/files/p-gen6-01.pdf',
    retrievedAt: '2026-08-04',
    kind: 'agency-data',
  },

  // ------------------------------------------------------------ court
  {
    id: 'src_tro',
    title:
      'Order and Memorandum Granting Motion for Temporary Restraining Order and Denying Motion for Summary Judgment',
    publisher:
      'Goodhue County District Court, First Judicial District (Hon. Patrick M. Biren), File No. 25-CV-25-2298',
    url: 'https://legalectric.org/f/2026/05/MCRO_25-CV-25-2298_Order-Granting-Motion_2026-05-22_20260526143544.pdf',
    publishedAt: '2026-05-22',
    retrievedAt: '2026-08-04',
    kind: 'court',
    locator: 'Order at 10, 13, 14-15',
    quote: 'MCEA has made a showing of possible success on the merits.',
    notes:
      'The court found POSSIBLE, not likely, success. Ryan requested a $5,000,000 bond; the court set the $2,000 statutory minimum, reasoning the TRO was partly due to the City’s failure to respond to Data Practices Act requests. Trial is set for February 2027.',
  },

  // ------------------------------------------------------------ grid / emissions
  {
    id: 'src_egrid',
    title: 'eGRID2023 (rev. 2) Subregion Output Emission Rates',
    publisher: 'US Environmental Protection Agency',
    url: 'https://www.epa.gov/egrid',
    publishedAt: '2025-06-12',
    retrievedAt: '2026-08-04',
    kind: 'agency-data',
    locator: 'Tables 1-4; subregions MROW and SRMW',
    notes:
      'Minnesota is in MROW. North Dakota, Iowa and Nebraska are ALSO in MROW, which is why they cannot serve as a grid comparator. Missouri (Ameren) is in SRMW.',
  },
  {
    id: 'src_xcel_release',
    title: 'Xcel Energy to Power New Google Data Center in Minnesota',
    publisher: 'Xcel Energy',
    url: 'https://newsroom.xcelenergy.com/news/xcel-energy-to-power-new-google-data-center-in-minnesota',
    publishedAt: '2026-02-24',
    retrievedAt: '2026-08-04',
    kind: 'company-statement',
    notes:
      'Source for the 1,400 MW wind / 200 MW solar / 300 MW long-duration storage figures and the $50M to Capacity*Connect. Does not state the facility’s load, and does not mention hourly carbon-free matching.',
  },
  {
    id: 'src_google_blog',
    title: 'A New Data Center in Pine Island, Minnesota',
    publisher: 'Google',
    url: 'https://blog.google/innovation-and-ai/infrastructure-and-cloud/global-network/data-center-pine-island/',
    publishedAt: '2026-02-24',
    retrievedAt: '2026-08-04',
    kind: 'company-statement',
    notes:
      'Gives no siting rationale referencing Minnesota’s 2040 clean electricity standard, and states no MW figure.',
  },
  {
    id: 'src_miso_dlol',
    title: 'Indicative DLOL Results, Planning Year 2025-2026',
    publisher: 'Midcontinent Independent System Operator',
    url: 'https://cdn.misoenergy.org/Indicative%20DLOL%20Results%20PY%202025-2026667100.pdf',
    retrievedAt: '2026-08-04',
    kind: 'filing',
    quote: 'All storage results assume 4-hour battery capability.',
    notes:
      'Direct Loss of Load accreditation takes effect for planning year 2028-29 (FERC approved 2024-10-25, Docket ER24-1638), which is when these resources energize. MISO publishes no accreditation value for storage longer than 4 hours.',
  },
  {
    id: 'src_miso_wind_solar',
    title: 'Wind and Solar Capacity Credit Report, Planning Year 2025-2026',
    publisher: 'Midcontinent Independent System Operator',
    url: 'https://cdn.misoenergy.org/PY%2025-26%20Wind%20and%20Solar%20Capacity%20Credit%20Report684294.pdf',
    publishedAt: '2025-03-01',
    retrievedAt: '2026-08-04',
    kind: 'filing',
    locator: 'Table 1-1, Local Resource Zone 1',
  },

  // ------------------------------------------------------------ water
  {
    id: 'src_dnr_water',
    title: 'Water Appropriation Permits, Installations and Reported Use, 1988-2024',
    publisher: 'Minnesota Department of Natural Resources',
    url: 'https://files.dnr.state.mn.us/waters/watermgmt_section/appropriations/mpars_index_permits_installations_uses.xlsx',
    publishedAt: '2025-09-16',
    retrievedAt: '2026-08-04',
    kind: 'agency-data',
    notes:
      'Reported withdrawals are self-reported annually by permit holders. Permitted maximums and measured actuals are different quantities and must never be compared across.',
  },

  // ------------------------------------------------------------ labor
  {
    id: 'src_dli_apprentice',
    title: 'Registered Apprenticeship Overview, Senate Labor Committee',
    publisher: 'Minnesota Department of Labor and Industry, Apprenticeship Minnesota',
    url: 'https://www.lrl.mn.gov/archive/minutes/senate/2025/labor/20250227/labor_20250227_DLI-Registered-Apprenticeship-Overview.pdf',
    publishedAt: '2025-02-26',
    retrievedAt: '2026-08-04',
    kind: 'agency-data',
    notes:
      '10,938 active apprentices statewide, of which construction is 89.2%.',
  },

  // ------------------------------------------------------------ context cited in prose
  {
    id: 'src_google_missouri',
    title: 'Google announces a $15 billion data center investment in Missouri',
    publisher: 'Reported coverage of Google’s Montgomery County, Missouri announcement',
    url: 'https://blog.google/',
    publishedAt: '2026-05-20',
    retrievedAt: '2026-08-04',
    kind: 'company-statement',
    notes:
      'Announced three months after Pine Island, in a state that is roughly 60% coal with no clean electricity standard. It makes the "built somewhere else" scenario concrete, and it is also the reason not to overclaim that Minnesota’s clean energy law is what attracted this investment.',
  },
  {
    id: 'src_loudoun',
    title: 'Loudoun County, Virginia data center tax base',
    publisher: 'Loudoun County, Virginia — adopted budget documents',
    url: 'https://www.loudoun.gov/budget',
    retrievedAt: '2026-08-04',
    kind: 'local-government',
    notes:
      'Cited only to show why Virginia figures are not a Minnesota analogue: Loudoun collects far more from data center equipment than from real property, and Minnesota does not tax equipment at all.',
  },

  // ------------------------------------------------------------ rates and ratepayers
  {
    id: 'src_xcel_8k_q2_2026',
    title: 'Q2 2026 Earnings Release, Exhibit 99.01 to Form 8-K',
    publisher: 'Xcel Energy Inc., filed with the US Securities and Exchange Commission',
    url: 'https://www.sec.gov/Archives/edgar/data/0000072903/000007290326000149/xcelearningsreleaseq22026.htm',
    publishedAt: '2026-07-30',
    retrievedAt: '2026-08-07',
    kind: 'company-statement',
    locator: 'Notes, under "NSP System — Large Load Agreement"',
    quote:
      'the agreement is expected to result in approximately $1.1 billion of benefits to NSP-Minnesota’s customers',
    notes:
      'The ONLY primary source for the benefit figure. It states no time period, no nominal-versus-present-value basis, no customer class split and no methodology. The same document carries the 2024 Minnesota rate case: a Commission decision of roughly $211 million over two years, and Minnesota electric rate base of $14 billion for 2026. The widely repeated "$1.5 billion over 15 years to residential and small business customers" version appears only in uncited trade coverage and is deliberately not used here.',
  },
  {
    id: 'src_eia_861',
    title: 'Annual Electric Power Industry Report, Form EIA-861 — Sales to Ultimate Customers, 2024',
    publisher: 'US Energy Information Administration',
    url: 'https://www.eia.gov/electricity/sales_revenue_price/',
    publishedAt: '2025-10-07',
    retrievedAt: '2026-08-07',
    kind: 'agency-data',
    locator:
      'File Sales_Ult_Cust_2024.xlsx, sheet "States"; Northern States Power Co - Minnesota, EIA utility ID 13781, state MN, bundled, observed',
    notes:
      'Every denominator in the bill-impact arithmetic. Minnesota jurisdiction only: the same utility’s North Dakota and South Dakota sales are excluded, because the Commission sets Minnesota retail rates. Residential 8,676,639 MWh and $1,473,375,300 against system totals of 27,722,191 MWh and $3,708,159,500, over 1,230,057 residential customers.',
  },
  {
    id: 'src_mpuc_large_load_tariff',
    title: 'Large load tariff, Docket E-002/M-25-289',
    publisher: 'Minnesota Public Utilities Commission',
    url: 'https://mn.gov/puc/activities/v-l-e-c/',
    publishedAt: '2026-05-15',
    retrievedAt: '2026-08-07',
    kind: 'filing',
    notes:
      'Approved 15 May 2026. Establishes a separate large-load rate class, a 15-year minimum contract term and an 80% demand-charge exit fee. The 15-year term used in this tool comes from here, not from the benefit disclosure, which states no term at all. Xcel filed the Incremental Cost Test workbook in this docket as a trade secret under Minn. Stat. § 13.37 subd. 1(b), so the model behind the benefit figure is not publicly checkable.',
  },
  {
    id: 'src_mpuc_esa_docket',
    title: 'Electric Service Agreement petition, Docket E-002/M-26-170',
    publisher: 'Minnesota Public Utilities Commission',
    url: 'https://mn.gov/puc/activities/v-l-e-c/',
    publishedAt: '2026-04-15',
    retrievedAt: '2026-08-07',
    kind: 'filing',
    notes:
      'The agreement the benefit figure depends on, filed 15 April 2026 with Echo Zone, LLC, a Google subsidiary. NOT APPROVED as of this writing: Xcel’s own July 2026 filing says a decision is expected in early 2027. Any figure derived from this agreement is conditional on an outcome that has not happened.',
  },
  {
    id: 'src_rate_literature',
    title: 'Impacts of Large Loads on Electricity Rates: a primer',
    publisher: 'The Brattle Group for the Energy Systems Integration Group',
    url: 'https://www.esig.energy/wp-content/uploads/2026/05/ESIG-Large-Load-Rate-Impacts-primer-WP-2026.pdf',
    publishedAt: '2026-05-01',
    retrievedAt: '2026-08-07',
    kind: 'academic',
    notes:
      'The neutral statement of the mechanism: where incremental revenue from a large customer exceeds the incremental cost of serving it, rates for existing customers tend to fall, and otherwise they rise. Cited for the caveat that the sign is not guaranteed. The empirical literature is genuinely split — an EPRI working paper (arXiv:2606.19777) finds residential prices fell, an MIT CEEPR paper published the same week finds they rose, and neither is peer reviewed.',
  },

  // ------------------------------------------------------------ our own workings
  {
    id: 'src_tool_methodology',
    title: 'whatweget.org — Methodology',
    publisher: 'Project Clover',
    url: '#methodology',
    retrievedAt: '2026-08-04',
    kind: 'academic',
    notes:
      'Modelling conventions and derivations that are ours rather than anyone else’s. Cited wherever a number is our arithmetic rather than a published figure.',
  },
];
